import { useCallback, useEffect, useRef } from 'react'
import useLatest from 'react-use/lib/useLatest'

import { useRegisterChildRootContainer } from './useRegisterChildRootContainer'

export function useOnNestedClickOutside<
  TTarget extends HTMLElement = HTMLElement,
  TContainer extends HTMLElement = HTMLElement,
>({
  onClickOutside,
  disabled,
}: {
  onClickOutside: (event: MouseEvent) => void
  disabled?: boolean
}) {
  const { ref, getChildRefs, ChildRegistrationContext } =
    useRegisterChildRootContainer<TContainer>()

  const targetRef = useRef<TTarget | null>(null)
  const targetRefCallback = useCallback((el: TTarget | null) => {
    targetRef.current = el
  }, [])

  const onClickOutsideRef = useLatest(onClickOutside)
  useEffect(() => {
    if (disabled) {
      return
    }

    const handleRootClick = (event: MouseEvent) => {
      const eventTarget =
        (event.composedPath?.()?.[0] as HTMLElement) ?? event.target
      if (targetRef.current?.contains(eventTarget as Node)) {
        return
      }

      if (getChildRefs().some((el) => el.contains(eventTarget as Node))) {
        return
      }

      onClickOutsideRef.current(event)
    }

    const opts = { capture: true }
    document.body.addEventListener('click', handleRootClick, opts)
    document.body.addEventListener('contextmenu', handleRootClick, opts)
    return () => {
      document.body.removeEventListener('click', handleRootClick, opts)
      document.body.removeEventListener('contextmenu', handleRootClick, opts)
    }
  }, [disabled, getChildRefs])

  return {
    ref,
    targetRef: targetRefCallback,
    ChildRegistrationContext,
  }
}
